import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import {
  Shield, Users, CheckCircle, Ticket,
  Star, Edit3, BarChart3,
  AlertCircle, ChevronRight, Activity, ArrowLeft
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { supabase } from "@/integrations/supabase/client";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

interface AdminStats {
  mrr: number;
  arr: number;
  totalUsers: number;
  activeSubscribers: number;
  pendingScreening: number;
  pendingReviews: number;
  pendingPayouts: number;
  pendingContent: number;
  churnRate: number;
  growth: number;
}

interface RecentActivity {
  id: string;
  title: string;
  description: string;
  time: string;
  timestamp: Date;
  severity: "info" | "warning" | "error";
  type: "registration" | "subscription" | "screening";
}

const formatTimeAgo = (dateString: string | null, t: (key: string, options?: Record<string, unknown>) => string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 60) {
    return t("admin.recentActivity.timeAgo.minutes", { count: diffMinutes || 1 });
  } else if (diffHours < 24) {
    return diffHours === 1 
      ? t("admin.recentActivity.timeAgo.hour") 
      : t("admin.recentActivity.timeAgo.hours", { count: diffHours });
  } else {
    return t("admin.recentActivity.timeAgo.days", { count: diffDays });
  }
};

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats>({
    mrr: 0,
    arr: 0,
    totalUsers: 0,
    activeSubscribers: 0,
    pendingScreening: 0,
    pendingReviews: 0,
    pendingPayouts: 0,
    pendingContent: 0,
    churnRate: 0,
    growth: 0
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin()) {
      navigate("/dashboard");
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    fetchAdminData();
    const interval = setInterval(fetchAdminData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAdminData = async () => {
    try {
      // Load unique users count using the database function
      const { data: uniqueUsersData } = await supabase.rpc('count_unique_users');
      const totalUsers = uniqueUsersData || 0;

      // Load active subscribers (only paid ones, excluding admin lifetime subscriptions)
      const { data: activeSubscriptions } = await supabase
        .from("subscriptions")
        .select("amount_paid, product_duration")
        .eq("status", "active");

      const subscribersCount = activeSubscriptions?.length || 0;

      // Calculate MRR correctly based on subscription duration
      // Monthly = full amount, Annual = amount / 12
      const mrr = activeSubscriptions?.reduce((sum, sub) => {
        const amount = Number(sub.amount_paid) || 0;
        if (amount === 0) return sum; // Skip free/admin subscriptions
        return sum + (sub.product_duration === 'annual' ? amount / 12 : amount);
      }, 0) || 0;

      // ARR is MRR * 12
      const arr = Math.round(mrr * 12);

      // Load pending screenings
      const { count: pendingScreeningCount } = await supabase
        .from("screening_applications")
        .select("*", { count: "exact", head: true })
        .eq("status", "pending");

      // Load pending reviews
      const { count: pendingReviewsCount } = await supabase
        .from("review_reports")
        .select("*", { count: "exact", head: true })
        .eq("status", "pending");

      // Load pending creator content
      const { count: pendingContentCount } = await supabase
        .from("creator_submissions")
        .select("*", { count: "exact", head: true })
        .eq("review_status", "pending");

      // Calculate real churn rate (canceled subscriptions in last 30 days / total active)
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      
      const { count: canceledCount } = await supabase
        .from("subscriptions")
        .select("*", { count: "exact", head: true })
        .eq("status", "cancelled")
        .gte("updated_at", thirtyDaysAgo);

      const churnRate = subscribersCount > 0 
        ? Math.round(((canceledCount || 0) / subscribersCount) * 100 * 10) / 10 
        : 0;

      // Calculate real growth rate (new users in last 30 days vs previous period)
      const { count: newUsersCount } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true })
        .gte("created_at", thirtyDaysAgo);

      const previousUsers = Math.max((totalUsers - (newUsersCount || 0)), 1); // Avoid division by 0
      const growth = Math.round(((newUsersCount || 0) / previousUsers) * 100);

      setStats({
        mrr: Math.round(mrr),
        arr,
        totalUsers,
        activeSubscribers: subscribersCount,
        pendingScreening: pendingScreeningCount || 0,
        pendingReviews: pendingReviewsCount || 0,
        pendingPayouts: 0,
        pendingContent: pendingContentCount || 0,
        churnRate,
        growth
      });

      // Fetch real recent activity data
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      
      // Query new users (last 7 days)
      const { data: newUsers } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, created_at')
        .gte('created_at', sevenDaysAgo)
        .order('created_at', { ascending: false })
        .limit(5);

      // Query new subscriptions (last 7 days)
      const { data: newSubs } = await supabase
        .from('subscriptions')
        .select('id, product_type, status, created_at')
        .gte('created_at', sevenDaysAgo)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(5);

      // Query approved screenings (last 7 days)
      const { data: approvedScreenings } = await supabase
        .from('screening_applications')
        .select('id, user_email, reviewed_at, status')
        .eq('status', 'approved')
        .gte('reviewed_at', sevenDaysAgo)
        .order('reviewed_at', { ascending: false })
        .limit(5);

      // Combine all activities
      const activities: RecentActivity[] = [];

      // Map new users
      newUsers?.forEach(user => {
        const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
        activities.push({
          id: `reg-${user.id}`,
          title: t("admin.recentActivity.newRegistration"),
          description: name || t("admin.recentActivity.newUser"),
          time: formatTimeAgo(user.created_at, t),
          timestamp: new Date(user.created_at),
          severity: "info",
          type: "registration"
        });
      });

      // Map subscriptions
      newSubs?.forEach(sub => {
        activities.push({
          id: `sub-${sub.id}`,
          title: t("admin.recentActivity.newSubscription"),
          description: `Piano ${sub.product_type}`,
          time: formatTimeAgo(sub.created_at, t),
          timestamp: new Date(sub.created_at!),
          severity: "info",
          type: "subscription"
        });
      });

      // Map approved screenings
      approvedScreenings?.forEach(screening => {
        activities.push({
          id: `screen-${screening.id}`,
          title: t("admin.recentActivity.screeningApproved"),
          description: screening.user_email || t("admin.recentActivity.professionalVerified"),
          time: formatTimeAgo(screening.reviewed_at, t),
          timestamp: new Date(screening.reviewed_at!),
          severity: "info",
          type: "screening"
        });
      });

      // Sort by timestamp descending and take latest 10
      activities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      setRecentActivity(activities.slice(0, 10));

      setLoading(false);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      setLoading(false);
    }
  };

  const adminSections = [
    {
      title: t("admin.sections.users"),
      icon: Users,
      href: "/admin/users",
      color: "blue",
      stats: stats.totalUsers,
      description: t("admin.sectionDescriptions.users")
    },
    {
      title: t("admin.sections.screening"),
      icon: CheckCircle,
      href: "/admin/screening",
      color: "green",
      stats: stats.pendingScreening,
      badge: stats.pendingScreening > 0 ? stats.pendingScreening : null,
      description: t("admin.sectionDescriptions.screening")
    },
    {
      title: t("admin.sections.coupons"),
      icon: Ticket,
      href: "/admin/coupons",
      color: "purple",
      description: t("admin.sectionDescriptions.coupons")
    },
    {
      title: t("admin.sections.reviews"),
      icon: Star,
      href: "/admin/reviews",
      color: "yellow",
      badge: stats.pendingReviews > 0 ? stats.pendingReviews : null,
      description: t("admin.sectionDescriptions.reviews")
    },
    {
      title: t("admin.sections.creator"),
      icon: Edit3,
      href: "/admin/creator-review",
      color: "pink",
      badge: stats.pendingContent > 0 ? stats.pendingContent : null,
      description: t("admin.sectionDescriptions.creator")
    },
    {
      title: t("admin.sections.analytics"),
      icon: BarChart3,
      href: "/admin/analytics",
      color: "orange",
      description: t("admin.sectionDescriptions.analytics")
    }
  ];

  const colorClasses: Record<string, string> = {
    blue: "bg-blue-100 text-blue-600 border-blue-300",
    green: "bg-green-100 text-green-600 border-green-300",
    purple: "bg-purple-100 text-purple-600 border-purple-300",
    yellow: "bg-yellow-100 text-yellow-600 border-yellow-300",
    pink: "bg-pink-100 text-pink-600 border-pink-300",
    orange: "bg-orange-100 text-orange-600 border-orange-300"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Activity className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Breadcrumb Navigation */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/dashboard">
                {t("dashboard.title")}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.title")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Back Button + Title */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("common.back")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          <div>
            <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
              <Shield className="w-10 h-10 text-error" />
              {t("admin.title")}
            </h1>
            <p className="text-muted-foreground mt-1">{t("admin.subtitle")}</p>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid md:grid-cols-6 gap-4 mb-8">
          <div className="bg-white border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-4">
            <div className="text-sm text-muted-foreground mb-1">{t("admin.kpi.mrr")}</div>
            <div className="text-2xl font-bold text-success">€{stats.mrr}</div>
            <div className="text-xs text-green-600">+{stats.growth}% {t("admin.kpi.growth")} ↑</div>
          </div>
          
          <div className="bg-white border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-4">
            <div className="text-sm text-muted-foreground mb-1">{t("admin.kpi.arr")}</div>
            <div className="text-2xl font-bold">€{stats.arr}</div>
          </div>
          
          <div className="bg-white border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-4">
            <div className="text-sm text-muted-foreground mb-1">{t("admin.kpi.users")}</div>
            <div className="text-2xl font-bold">{stats.totalUsers}</div>
          </div>
          
          <div className="bg-white border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-4">
            <div className="text-sm text-muted-foreground mb-1">{t("admin.kpi.subscribers")}</div>
            <div className="text-2xl font-bold">{stats.activeSubscribers}</div>
          </div>
          
          <div className="bg-white border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-4">
            <div className="text-sm text-muted-foreground mb-1">{t("admin.kpi.churn")}</div>
            <div className="text-2xl font-bold text-error">{stats.churnRate}%</div>
          </div>
          
          <div className="bg-white border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-4">
            <div className="text-sm text-muted-foreground mb-1">{t("admin.kpi.pending")}</div>
            <div className="text-2xl font-bold text-warning">
              {stats.pendingScreening + stats.pendingReviews + stats.pendingPayouts + stats.pendingContent}
            </div>
          </div>
        </div>

        {/* Admin Sections Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {adminSections.map((section, index) => {
            const Icon = section.icon;
            return (
              <Link
                key={index}
                to={section.href}
                className="relative bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[8px_8px_0px_#101322] transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-lg border-[2px] ${colorClasses[section.color]}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                
                <h3 className="font-heading text-lg font-bold mb-1 flex items-center gap-2">
                  {section.title}
                  {section.badge && (
                    <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-sm font-bold border-[2px] border-dark" style={{ backgroundColor: '#ef4444', color: '#ffffff' }}>
                      {section.badge}
                    </span>
                  )}
                </h3>
                
                {section.stats !== undefined && (
                  <div className="text-2xl font-bold text-primary mb-2">
                    {section.stats}
                  </div>
                )}
                
                <p className="text-sm text-muted-foreground">
                  {section.description}
                </p>
              </Link>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6 mb-8">
          <h2 className="font-heading text-2xl font-bold mb-4">
            {t("admin.quickActions.title")}
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <BrutalButton
              variant="primary"
              size="default"
              onClick={() => navigate('/admin/coupons')}
              className="w-full"
            >
              🎟️ {t("admin.quickActions.createCoupon")}
            </BrutalButton>
            
            <BrutalButton
              variant="secondary"
              size="default"
              onClick={() => navigate('/admin/monitoring')}
              className="w-full"
            >
              📋 {t("admin.quickActions.viewLogs")}
            </BrutalButton>
            
            <BrutalButton
              variant="danger"
              size="default"
              onClick={() => navigate('/admin/settings?tab=modes')}
              className="w-full"
            >
              🔧 {t("admin.quickActions.maintenanceMode")}
            </BrutalButton>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white border-[3px] border-dark shadow-[6px_6px_0px_#101322] p-6">
          <h2 className="font-heading text-2xl font-bold mb-4">
            {t("admin.recentActivity.title")}
          </h2>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {recentActivity.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {t("admin.recentActivity.noActivity")}
              </p>
            ) : (
              recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-center justify-between py-3 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      activity.severity === "error" 
                        ? "bg-red-100 text-red-600" 
                        : activity.severity === "warning" 
                        ? "bg-yellow-100 text-yellow-600" 
                        : "bg-green-100 text-green-600"
                    }`}>
                      <AlertCircle className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-semibold">{activity.title}</p>
                      <p className="text-sm text-muted-foreground">{activity.description}</p>
                    </div>
                  </div>
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {activity.time}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
